import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, Loader2, Music, ListMusic, Trash2, Globe, Lock } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import BeatPlayer from "../components/BeatPlayer";
import ShareButton from "../components/ShareButton";
import { useAuth } from "../App";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PlaylistPage = () => {
  const { playlistId } = useParams();
  const [playlist, setPlaylist] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchPlaylist = async () => {
    try {
      const headers = {};
      const token = localStorage.getItem("token");
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await axios.get(`${API}/playlists/${playlistId}`, { headers, withCredentials: true });
      setPlaylist(res.data);
    } catch (e) {
      console.error("Error fetching playlist:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPlaylist(); }, [playlistId]);

  const handleRemoveBeat = async (beatId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/playlists/${playlistId}/beats/${beatId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Beat removed from playlist");
      fetchPlaylist();
    } catch (e) {
      toast.error("Failed to remove beat");
    }
  };

  const isOwner = user && playlist && user.user_id === playlist.user_id;

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 text-neon-cyan animate-spin" /></div>;
  }

  if (!playlist) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Music className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-400">Playlist not found</p>
          <Link to="/discover" className="text-neon-cyan text-sm mt-2 block">Back to Discover</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-10 px-6" data-testid="playlist-page">
      <div className="max-w-7xl mx-auto">
        <Link to="/discover" className="inline-flex items-center gap-2 text-zinc-500 hover:text-neon-cyan mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Discover
        </Link>

        <div className="glass p-8 mb-8 flex items-center gap-6">
          <div className="w-20 h-20 bg-neon-cyan/10 flex items-center justify-center flex-shrink-0">
            <ListMusic className="w-10 h-10 text-neon-cyan" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h1 className="font-heading text-3xl md:text-4xl uppercase tracking-tight">{playlist.name}</h1>
              {playlist.is_public ? <Globe className="w-4 h-4 text-zinc-500" /> : <Lock className="w-4 h-4 text-zinc-500" />}
              <div className="ml-auto">
                <ShareButton type="playlist" id={playlistId} title={`${playlist.name} - The Gas Station`} description={`${playlist.beat_details?.length || 0} beats`} />
              </div>
            </div>
            {playlist.description && <p className="text-zinc-400 text-sm">{playlist.description}</p>}
            <p className="text-xs text-zinc-600 mt-2">{playlist.beat_details?.length || 0} beats</p>
          </div>
        </div>

        {(!playlist.beat_details || playlist.beat_details.length === 0) ? (
          <div className="glass p-12 text-center">
            <ListMusic className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400 mb-2">This playlist is empty</p>
            <p className="text-xs text-zinc-600">Browse beats and add them to this playlist</p>
            <Link to="/beats">
              <Button className="mt-4 bg-neon-cyan text-black font-bold">Browse Beats</Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {playlist.beat_details.map((beat, index) => (
              <motion.div key={beat.beat_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.03 }} className="relative">
                <BeatPlayer beat={beat} />
                {isOwner && (
                  <button
                    onClick={() => handleRemoveBeat(beat.beat_id)}
                    className="absolute top-3 right-3 p-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-colors"
                    data-testid={`remove-from-playlist-${beat.beat_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlaylistPage;
